import type { SyntheticEvent } from 'react';

export function About() {
  function handleLeaderImageError(event: SyntheticEvent<HTMLImageElement>) {
    const target = event.currentTarget;
    target.onerror = null;
    target.src = '/img/logo.svg';
    target.classList.add('person-photo-fallback');
  }

  return (
    <div className="about-page">
      <section className="section">
        <div className="container">
          <div className="section-head section-head-about reveal">
            <p className="eyebrow">About ExpanseFT</p>
            <h1>Built by operators. Designed for scale.</h1>
            <p>
              We have spent years tackling financial technology challenges with startups, emerging
              companies, and established brands looking to launch and scale modern payment programs.
            </p>
          </div>
        </div>
      </section>

      <section className="section section-soft">
        <div className="container">
          <div className="section-head section-head-about reveal">
            <h2>Our Story</h2>
            <p>
              A track record of building durable fintech programs through platform depth and
              execution discipline.
            </p>
          </div>
          <div className="timeline-grid reveal reveal-delay-1">
            <article className="timeline-card">
              <h3>2007 - 2012</h3>
              <p>
                Prepaid Ventures is founded and NexsCard grows from a regional launch into a
                national brand.
              </p>
            </article>
            <article className="timeline-card">
              <h3>2014 - 2019</h3>
              <p>
                We expand into white-label program management and invest deeply in in-house
                technology and operations.
              </p>
            </article>
            <article className="timeline-card">
              <h3>2022 - Today</h3>
              <p>
                Prepaid Ventures becomes Expanse Financial Technologies, focused on modern, modular
                fintech programs.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head section-head-about reveal">
            <h2>Leadership</h2>
            <p>Experienced builders and operators guiding product, partnerships, and execution.</p>
          </div>
          <div className="people-grid reveal reveal-delay-1">
            <article className="person-card">
              <img
                className="person-photo"
                src="/img/michael-gaspar.png"
                alt="Michael Gaspar"
                onError={handleLeaderImageError}
              />
              <h3>Michael Gaspar</h3>
              <p className="person-role">CEO</p>
              <a
                className="person-linkedin"
                href="https://www.linkedin.com/in/mgaspar213"
                target="_blank"
                rel="noreferrer"
                aria-label="Michael Gaspar on LinkedIn"
              >
                <img src="/img/linkedin.png" alt="" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </article>
            <article className="person-card">
              <img
                className="person-photo person-photo-tammy"
                src="/img/tammy-webb.png"
                alt="Tammy Webb"
                onError={handleLeaderImageError}
              />
              <h3>Tammy Webb</h3>
              <p className="person-role">EVP, Operations</p>
              <a
                className="person-linkedin"
                href="https://www.linkedin.com/in/tammy-webb-aap-a68ab282/"
                target="_blank"
                rel="noreferrer"
                aria-label="Tammy Webb on LinkedIn"
              >
                <img src="/img/linkedin.png" alt="" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </article>
            <article className="person-card">
              <img
                className="person-photo"
                src="/img/timothy-antchoutine.jpg"
                alt="Timothy Antchoutine"
                onError={handleLeaderImageError}
              />
              <h3>Timothy Antchoutine</h3>
              <p className="person-role">EVP, Platform & Technology</p>
              <a
                className="person-linkedin"
                href="https://www.linkedin.com/in/timothy-antchoutine-9890bb7b"
                target="_blank"
                rel="noreferrer"
                aria-label="Timothy Antchoutine on LinkedIn"
              >
                <img src="/img/linkedin.png" alt="" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </article>
            <article className="person-card">
              <img
                className="person-photo"
                src="/img/cheryl-fajardo.jpg"
                alt="Cheryl Fajardo"
                onError={handleLeaderImageError}
              />
              <h3>Cheryl Fajardo</h3>
              <p className="person-role">SVP, Sales</p>
              <a
                className="person-linkedin"
                href="https://www.linkedin.com/in/cheryl-fajardo-5164552940/"
                target="_blank"
                rel="noreferrer"
                aria-label="Cheryl Fajardo on LinkedIn"
              >
                <img src="/img/linkedin.png" alt="" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </article>
            <article className="person-card">
              <img
                className="person-photo"
                src="/img/alex-sbato.jpg"
                alt="Alex Sabato"
                onError={handleLeaderImageError}
              />
              <h3>Alex Sabato</h3>
              <p className="person-role">Contact Center Manager</p>
              <a
                className="person-linkedin"
                href="https://www.linkedin.com/in/alexander-sabato/"
                target="_blank"
                rel="noreferrer"
                aria-label="Alex Sabato on LinkedIn"
              >
                <img src="/img/linkedin.png" alt="" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </article>
          </div>
        </div>
      </section>

      <section className="section section-soft">
        <div className="container">
          <div className="section-head section-head-about reveal">
            <h2>Our Board</h2>
            <p>Seasoned leaders across finance, investments, operations, and hospitality.</p>
          </div>
          <div className="board-grid reveal reveal-delay-1">
            <article className="board-card">
              <h3>Michael Pruzan</h3>
              <p>
                Michael Pruzan served as a Partner at Soros Private Equity Partners, from 1999 to
                February 2004. Prior to joining SPEP, Michael spent over eleven years at Goldman,
                Sachs & Co. in Mergers and Acquisitions, Equity Capital Markets and Principal
                Investments. Mr. Pruzan received his BA from Wesleyan University.
              </p>
            </article>
            <article className="board-card">
              <h3>Michael Asch</h3>
              <p>
                Michael Asch is a managing member of MNM Properties, LLC., a real estate & angel
                investment company. MNM owns and operates properties in NYC, Chicago, Miami, and
                Charleston, as well as holds investments in small private startup companies. Mr.
                Asch is the President of a restaurant consulting company, Asch Consulting Corp.,
                that operates over a dozen restaurants for the past 25 years. These include many
                iconic establishments in New York, Chicago, and Charleston SC. Michael currently
                sits on or holds an observer role on several Boards, across many industries. Michael
                has a BA from Tufts University, and graduated from Horace Mann High School in Bronx
                NY.
              </p>
            </article>
            <article className="board-card">
              <h3>Sasha Grutman</h3>
              <p>
                Sasha Grutman has served as a Partner at THLee Putnam Ventures, a $1 billion growth
                equity fund where he co-led the firm's financial services investments in specialty
                finance, insurance services, and electronic capital markets investments. Prior to
                his tenure at THLee Putnam Ventures, he was a Managing Director at Citigroup where
                he oversaw the firm's on balance sheet financial services private equity holdings as
                well as managed the bank's $6 billion private equity fund-of-funds business. He
                currently is a Board Member of ExpanseFT, a Special Advisor to the boards of a
                number of specialty finance companies, and a Board Director of Evening Land
                Vineyards.
              </p>
            </article>
            <article className="board-card">
              <h3>Mitchell Banchik</h3>
              <p>
                Mitchell Banchik is a managing member of Embe Properties, LLC, a real estate
                investment company that owns and operates properties in NYC, Chicago, Miami, and
                Charleston. Mitchell is the President of a restaurant consulting company that
                operates over 20 restaurants and has been in business for over 20 years with many
                iconic establishments in New York, Chicago, and Charleston. Mr. Banchik currently
                holds a seat as the Treasurer of NYC Hospitality Alliance as well as a Degree in
                Finance from NYU.
              </p>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
}
